<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Badge from 'primevue/badge'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import CardComponent from '@/components/cards/CardComponent.vue'
import TextInputDefault from '@/components/forms/TextInputDefault.vue'
import BaseButton from '@/components/Buttons/BaseButton.vue'
import ModalDefault from '@/components/Modals/ModalDefault.vue'
import HeadingDefault from '@/components/Heading/HeadingOne.vue'
import SelectOptionPrime from '@/components/forms/SelectOption/SelectOptionPrime.vue'
import StatsCards from '@/components/cards/StatsCards.vue'
import UserModal from '../Modals/UserModal.vue'
import DeleteUserModal from '../Modals/DeleteUserModal.vue'
import CompanyBadge from '@/components/badges/CompanyBadge.vue'
import type { User, UserStatus, UserFormData } from '@/types/user'
import type { UserType } from '@/utils/roleHandler'
import {
  UsersGroupIcon,
  AdminIcon,
  AgentIcon,
  EngineerIcon,
  // EditIcon,
  DeleteIcon,
  PauseIcon,
  CheckIcon,
  LockIcon,
  UnlockIcon,
  ClockIcon,
  RefreshIcon,
  UserGroupIcon
} from '@/icons'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import PermanentDeleteModal from '../Modals/PermanentDeleteModal.vue'
import type { AxiosError } from 'axios'
import api from '@/services/api'
import { notificationService } from '@/services/notificationService'
import EmptyState from '@/components/common/EmptyState.vue'
import ErrorState from '@/components/common/ErrorState.vue'
import LoaderDefault from '@/components/common/LoaderDefault.vue'

// Define ApiError type that matches UserModal's expectation
interface ApiError {
  message?: string;
  response?: {
    data?: {
      field?: string;
      message?: string;
      [key: string]: unknown;
    };
  };
  [key: string]: unknown;
}

// If ClientIcon doesn't exist, create a simple fallback
const ClientIcon = EngineerIcon

// Reactive state
const users = ref<User[]>([])
const filteredUsers = ref<User[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const searchFilter = ref('')
const userTypeFilter = ref('')
const statusFilter = ref('')
const companyFilter = ref('') // ✅ New company filter

// Modal states
const showUserModal = ref(false)
const showDeleteModal = ref(false)
const showPermanentDeleteModal = ref(false)
const showResendInvitationModal = ref(false)
const showConfirmationModal = ref(false)
const confirmationMessage = ref('')
const confirmationAction = ref<() => void>(() => {})
const selectedUser = ref<User | null>(null)
const userToToggle = ref<User | null>(null)
const userToPermanentDelete = ref<User | null>(null)
const userToResendInvitation = ref<User | null>(null)

// Add ref for UserModal
const userModalRef = ref<{
  getFormData: () => Partial<UserFormData> | null
}>()

const router = useRouter()
const authStore = useAuthStore()

const isDevMode = ref(false)

// Check if we're in development mode
onMounted(() => {
  isDevMode.value = import.meta.env.DEV

  // Check if already logged in
  // if (authStore.isAuthenticated) {
  //   const userType = authStore.user?.userType || getUserType()
  //   console.log('👤 Already logged in as:', userType)
  //   router.push('/dashboard')
  // }
})

// Filter options
const userTypeOptions = ref([
  { label: 'All User Types', value: '' },
  { label: 'Admin', value: 'admin' },
  { label: 'Agent', value: 'agent' },
  { label: 'Engineer', value: 'engineer' },
  { label: 'Client', value: 'client' },
])

const statusOptions = ref([
  { label: 'All Status', value: '' },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Pending', value: 'pending' },
  { label: 'Suspended', value: 'suspended' },
])

// ✅ New company filter options
const companyOptions = ref([
  { label: 'All Companies', value: '' },
  { label: 'Max Stationary', value: 'maxstationary' },
  { label: 'OST Nasik', value: 'ostnasik' },
  { label: 'Shradhha Ent', value: 'shradhha-ent' },
])

// API error state
const apiError = ref<ApiError | null>(null)

// Helper function to convert AxiosError to ApiError
const axiosErrorToApiError = (axiosError: AxiosError): ApiError => {
  return {
    message: axiosError.message,
    response: axiosError.response ? {
      data: axiosError.response.data as {
        field?: string;
        message?: string;
        [key: string]: unknown;
      }
    } : undefined,
    code: axiosError.code,
    config: axiosError.config,
    request: axiosError.request,
    isAxiosError: axiosError.isAxiosError,
    toJSON: axiosError.toJSON
  }
}

// Helper function to get name - handles both camelCase and snake_case
const getUserName = (user: User, field: 'firstName' | 'lastName'): string => {
  // Try camelCase first, then snake_case, then fallback
  if (field === 'firstName') {
    return user.firstName || (user as { first_name?: string }).first_name || ''
  } else {
    return user.lastName || (user as { last_name?: string }).last_name || ''
  }
}

// Helper function to get display name
const getDisplayName = (user: User): string => {
  const firstName = getUserName(user, 'firstName')
  const lastName = getUserName(user, 'lastName')
  const fullName = `${firstName} ${lastName}`.trim()
  return fullName || user.username || user.email || 'No name'
}

// Helper function to get initials
const getInitials = (user: User): string => {
  const firstName = getUserName(user, 'firstName')
  const lastName = getUserName(user, 'lastName')
  return `${firstName.charAt(0) || ''}${lastName.charAt(0) || ''}`.toUpperCase() || 'U'
}

// Computed properties
const userStats = computed(() => {
  const stats: Record<string, number> = {
    total: users.value.length,
    admin: 0,
    agent: 0,
    engineer: 0,
    client: 0,
    active: 0,
    inactive: 0,
    pending: 0,
    suspended: 0,
  }

  users.value.forEach((user) => {
    // Count user types
    if (user.userType) {
      const userType = user.userType as string
      if (stats[userType] !== undefined) {
        stats[userType]++
      }
    }

    // Count statuses
    if (user.status) {
      const status = user.status as string
      if (stats[status] !== undefined) {
        stats[status]++
      }
    }
  })

  return stats
})

// Filter users based on search, user type, status, and company
const applyFilters = () => {
  let filtered = users.value

  // ✅ Apply company filter
  if (companyFilter.value) {
    filtered = filtered.filter(
      (user) => user.company === companyFilter.value,
    )
  }

  // Apply user type filter
  if (userTypeFilter.value) {
    filtered = filtered.filter(
      (user) => user.userType === userTypeFilter.value,
    )
  }

  // Apply status filter
  if (statusFilter.value) {
    filtered = filtered.filter((user) => user.status === statusFilter.value)
  }

  // Apply search filter - handles both camelCase and snake_case
  if (searchFilter.value) {
    const searchTerm = searchFilter.value.toLowerCase()
    filtered = filtered.filter(
      (user) =>
        getUserName(user, 'firstName').toLowerCase().includes(searchTerm) ||
        getUserName(user, 'lastName').toLowerCase().includes(searchTerm) ||
        user.email?.toLowerCase().includes(searchTerm) ||
        user.phone?.toLowerCase().includes(searchTerm) ||
        user.department?.toLowerCase().includes(searchTerm) ||
        user.username?.toLowerCase().includes(searchTerm) ||
        user.company?.toLowerCase().includes(searchTerm) // ✅ Include company in search
    )
  }

  filteredUsers.value = filtered
}

// Format date
const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A'

  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return dateString

    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch (error) {
    console.error('Date formatting error:', error)
    return dateString
  }
}

// Format date with time
const formatDateTime = (dateString: string): string => {
  if (!dateString) return 'Never'

  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return dateString

    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch (error) {
    console.error('DateTime formatting error:', error)
    return dateString
  }
}

// Format invitation expiration
const formatInvitationExpiration = (dateString: string): string => {
  if (!dateString) return 'Expired'

  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return dateString

    const now = new Date()
    const diffMs = date.getTime() - now.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

    if (diffMs <= 0) {
      return 'Expired'
    } else if (diffHours < 1) {
      return `${diffMinutes}m left`
    } else if (diffHours < 24) {
      return `${diffHours}h left`
    } else {
      return formatDate(dateString)
    }
  } catch (error) {
    console.error('Invitation expiration formatting error:', error)
    return dateString
  }
}

// Get user type badge class
const getUserTypeBadgeClass = (userType: UserType) => {
  const classMap: Record<string, string> = {
    admin: 'bg-purple-100 text-purple-800 border border-purple-200',
    agent: 'bg-blue-100 text-blue-800 border border-blue-200',
    engineer: 'bg-green-100 text-green-800 border border-green-200',
    client: 'bg-orange-100 text-orange-800 border border-orange-200',
  }
  return classMap[userType] || 'bg-gray-100 text-gray-800 border border-gray-200'
}

// Get user type label
const getUserTypeLabel = (userType: UserType) => {
  const labelMap: Record<string, string> = {
    admin: 'Administrator',
    agent: 'Agent',
    engineer: 'Engineer',
    client: 'Client',
  }
  return labelMap[userType] || userType
}

// Get status badge class
const getStatusBadgeClass = (status: UserStatus) => {
  const classMap: Record<string, string> = {
    active: 'bg-green-100 text-green-800 border border-green-200',
    inactive: 'bg-gray-100 text-gray-800 border border-gray-200',
    pending: 'bg-blue-100 text-blue-800 border border-blue-200',
    suspended: 'bg-red-100 text-red-800 border border-red-200',
  }
  return classMap[status] || 'bg-gray-100 text-gray-800 border border-gray-200'
}

// Get status label
const getStatusLabel = (status: UserStatus) => {
  const labelMap: Record<string, string> = {
    active: 'Active',
    inactive: 'Inactive',
    pending: 'Pending Setup',
    suspended: 'Suspended',
  }
  return labelMap[status] || status
}

// Get appropriate icon for delete/toggle button
const getToggleIcon = (status: UserStatus) => {
  switch (status) {
    case 'active':
      return LockIcon
    case 'inactive':
    case 'suspended':
      return UnlockIcon
    case 'pending':
      return null
    default:
      return DeleteIcon
  }
}

// Get appropriate tooltip text
const getToggleTooltip = (status: UserStatus) => {
  switch (status) {
    case 'active':
      return 'Lock User'
    case 'inactive':
      return 'Activate User'
    case 'suspended':
      return 'Reactivate User'
    case 'pending':
      return 'Pending password setup'
    default:
      return 'Change Status'
  }
}

// Helper function to get auth token
const getAuthToken = (): string => {
  // Try multiple sources
  const tokenFromLocalStorage = localStorage.getItem('auth_token')
  const tokenFromSessionStorage = sessionStorage.getItem('auth_token')
  const tokenFromAuthStore = authStore.token

  // Priority: authStore token > localStorage token > sessionStorage token
  return tokenFromAuthStore || tokenFromLocalStorage || tokenFromSessionStorage || ''
}

// API Functions
const fetchUsers = async () => {
  try {
    loading.value = true
    error.value = null

    console.log('🔍 Fetching users...')

    const token = getAuthToken()
    if (!token) {
      throw new Error('No authentication token found. Please log in.')
    }

    const response = await api.get('/api/users')

    console.log('✅ Users fetched successfully')

    if (response.data.success && response.data.data) {
      users.value = response.data.data
      console.log(`✅ Loaded ${users.value.length} users`)
    } else if (response.data.users) {
      users.value = response.data.users
      console.log(`✅ Loaded ${users.value.length} users`)
    } else {
      users.value = []
      console.log('ℹ️ No users found')
    }

    applyFilters()
  } catch (err: unknown) {
    console.error('❌ Error fetching users:', err)

    const axiosError = err as AxiosError
    if (axiosError.response) {
      if (axiosError.response.status === 401) {
        // Token is invalid or expired
        console.log('❌ Token invalid/expired')
        authStore.logout()
        router.push('/')
        error.value = 'Session expired. Please log in again.'
        notificationService.error('Session expired. Please log in again.')
      } else {
        error.value = `Server error: ${axiosError.response.status} - ${(axiosError.response.data as { message?: string })?.message || 'Unknown error'}`
      }
    } else if (axiosError.request) {
      error.value = 'No response from server. Make sure backend is running.'
      notificationService.error('No response from server. Make sure backend is running.')
    } else {
      error.value = `Request failed: ${axiosError.message}`
      notificationService.error(`Request failed: ${axiosError.message}`)
    }

    users.value = []
    filteredUsers.value = []
  } finally {
    loading.value = false
  }
}

const createUser = async (userData: Partial<UserFormData>): Promise<User> => {
  const response = await api.post('/api/users', userData)
  return response.data.data || response.data.user
}

const updateUser = async (id: string, userData: Partial<UserFormData>): Promise<User> => {
  const response = await api.put(`/api/users/${id}`, userData)
  return response.data.data || response.data.user
}

const updateUserStatus = async (id: string, status: UserStatus): Promise<void> => {
  await api.put(`/api/users/${id}/status`, { status })
}

const deleteUserPermanently = async (id: string): Promise<void> => {
  await api.delete(`/api/users/${id}/permanent`)
}

const resendInvitation = async (userId: string): Promise<{
  success: boolean;
  message: string;
  data?: {
    id: string;
    email: string;
    invitationSentAt: Date;
    invitationExpiresAt: Date;
  }
}> => {
  const response = await api.post(`/api/users/${userId}/resend-invitation`, {})
  return response.data
}

// Modal handlers
const openAddUserModal = () => {
  selectedUser.value = null
  apiError.value = null // Clear any previous errors
  showUserModal.value = true
}

// const openEditUserModal = (user: User) => {
//   selectedUser.value = user
//   apiError.value = null // Clear any previous errors
//   showUserModal.value = true
// }

// UPDATED: Handle save user with validation from UserModal
const handleSaveUser = async () => {
  try {
    loading.value = true
    error.value = null
    apiError.value = null // Clear previous API errors

    // Get validated form data from UserModal
    const userData = userModalRef.value?.getFormData()

    if (!userData) {
      // Validation failed - errors are already shown in UserModal
      loading.value = false
      return
    }

    console.log('Saving user data:', userData)

    if (selectedUser.value) {
      // Update existing user
      await updateUser(selectedUser.value.id, userData)
      notificationService.success('User updated successfully!')
    } else {
      // Create new user
      const createData: Partial<UserFormData> = {
        ...userData,
        status: 'active' as UserStatus,
      }

      await createUser(createData as UserFormData)
      notificationService.success('User created successfully!')
    }

    await fetchUsers()
    closeUserModal()
  } catch (err: unknown) {
    console.error('Error saving user:', err)
    const axiosError = err as AxiosError

    // Convert AxiosError to ApiError for UserModal
    apiError.value = axiosErrorToApiError(axiosError)

    // Also show notification
    const errorMessage = (axiosError.response?.data as { message?: string })?.message || axiosError.message || 'Unknown error'
    notificationService.error(`Failed to save user: ${errorMessage}`)
  } finally {
    loading.value = false
  }
}

const closeUserModal = () => {
  showUserModal.value = false
  selectedUser.value = null
  apiError.value = null // Clear the API error when modal closes
}

// Open modal for status toggle (activate/deactivate)
const openToggleModal = (user: User) => {
  userToToggle.value = user
  showDeleteModal.value = true
}

// Handle status toggle (activate/deactivate)
const handleToggleStatus = async () => {
  if (!userToToggle.value) return

  const currentStatus = userToToggle.value.status
  const newStatus = currentStatus === 'active' ? 'inactive' : 'active'
  const action = newStatus === 'active' ? 'activate' : 'deactivate'

  try {
    loading.value = true
    error.value = null

    await updateUserStatus(userToToggle.value.id, newStatus)
    await fetchUsers()
    notificationService.success(`User ${action}d successfully!`)
  } catch (err: unknown) {
    console.error(`Error ${action}ing user:`, err)
    const axiosError = err as AxiosError
    const errorMessage = (axiosError.response?.data as { message?: string })?.message || axiosError.message || 'Unknown error'
    notificationService.error(`Failed to ${action} user: ${errorMessage}`)
  } finally {
    loading.value = false
    closeToggleModal()
  }
}

const closeToggleModal = () => {
  showDeleteModal.value = false
  userToToggle.value = null
}

// Open resend invitation modal
const openResendInvitationModal = (user: User) => {
  userToResendInvitation.value = user
  showResendInvitationModal.value = true
}

// Resend invitation email
const handleResendInvitation = async () => {
  if (!userToResendInvitation.value) return

  try {
    loading.value = true
    error.value = null

    const result = await resendInvitation(userToResendInvitation.value.id)

    if (result.success) {
      notificationService.success('Invitation resent successfully!')
      await fetchUsers()
    } else {
      notificationService.error(`Failed to resend invitation: ${result.message}`)
    }
  } catch (err: unknown) {
    console.error('Error resending invitation:', err)
    const axiosError = err as AxiosError
    const errorMessage = (axiosError.response?.data as { message?: string })?.message || axiosError.message || 'Unknown error'
    notificationService.error(`Failed to resend invitation: ${errorMessage}`)
  } finally {
    loading.value = false
    closeResendInvitationModal()
  }
}

const closeResendInvitationModal = () => {
  showResendInvitationModal.value = false
  userToResendInvitation.value = null
}

// Permanent delete handlers
const openPermanentDeleteModal = (user: User) => {
  userToPermanentDelete.value = user
  showPermanentDeleteModal.value = true
}

const handlePermanentDeleteUser = async () => {
  if (!userToPermanentDelete.value) return

  try {
    loading.value = true
    error.value = null

    await deleteUserPermanently(userToPermanentDelete.value.id)
    await fetchUsers()
    notificationService.success('User permanently deleted!')
  } catch (err: unknown) {
    console.error('Error permanently deleting user:', err)
    const axiosError = err as AxiosError
    const errorMessage = (axiosError.response?.data as { message?: string })?.message || axiosError.message || 'Unknown error'
    notificationService.error(`Failed to permanently delete user: ${errorMessage}`)
  } finally {
    loading.value = false
    closePermanentDeleteModal()
  }
}

const closePermanentDeleteModal = () => {
  showPermanentDeleteModal.value = false
  userToPermanentDelete.value = null
}

const handleConfirmation = () => {
  confirmationAction.value()
  showConfirmationModal.value = false
}

const closeConfirmationModal = () => {
  showConfirmationModal.value = false
  confirmationMessage.value = ''
  confirmationAction.value = () => {}
}

// Refresh users
const refreshUsers = async () => {
  await fetchUsers()
  notificationService.info('Users refreshed!')
}

// Debug function to test backend connection
const testBackendConnection = async () => {
  try {
    loading.value = true
    console.log('🔍 Testing backend connection...')

    // Try health check first
    const healthResponse = await api.get('/health')
    console.log('✅ Health check:', healthResponse.data)

    // Try to fetch users
    await fetchUsers()

    if (!error.value) {
      notificationService.success(`Backend connection successful! Found ${users.value.length} users`)
    } else {
      notificationService.error(`Backend error: ${error.value}`)
    }
  } catch (err) {
    console.error('Connection test error:', err)
    notificationService.error('Failed to connect to backend. Make sure the server is running')
  } finally {
    loading.value = false
  }
}

// Test login function
const testLogin = async () => {
  try {
    loading.value = true
    console.log('🔐 Testing login...')

    const loginResponse = await api.post('/api/auth/login', {
      email: 'admin@pokasu.com',
      password: 'Admin@123'
    })

    console.log('✅ Login successful')

    if (loginResponse.data.token) {
      localStorage.setItem('auth_token', loginResponse.data.token)
      if (authStore.setToken) {
        authStore.setToken(loginResponse.data.token)
      }
      if (authStore.setUser && loginResponse.data.user) {
        authStore.setUser(loginResponse.data.user)
      }
      notificationService.success('Logged in successfully!')

      await fetchUsers()
    }
  } catch (err: unknown) {
    console.error('❌ Login test failed:', err)
    const axiosError = err as AxiosError
    const errorMessage = (axiosError.response?.data as { message?: string })?.message || axiosError.message || 'Unknown error'
    notificationService.error(`Login failed: ${errorMessage}`)
  } finally {
    loading.value = false
  }
}

// ✅ Updated clearFilters function
const clearFilters = () => {
  searchFilter.value = ''
  userTypeFilter.value = ''
  statusFilter.value = ''
  companyFilter.value = ''
  applyFilters()
}

// Debug auth state
const debugAuth = () => {
  console.log('🔍 DEBUG Auth State:')
  console.log('LocalStorage auth_token:', localStorage.getItem('auth_token') ? 'Present' : 'Missing')
  console.log('SessionStorage auth_token:', sessionStorage.getItem('auth_token') ? 'Present' : 'Missing')
  console.log('AuthStore token:', authStore.token ? 'Present' : 'Missing')
  console.log('AuthStore isAuthenticated:', authStore.isAuthenticated)
  console.log('AuthStore user:', authStore.user)

  const token = getAuthToken()
  console.log('Final token:', token ? `Present (${token.length} chars)` : 'Missing')

  notificationService.info('Check browser console for auth debug info')
}

// Initialize
onMounted(() => {
  console.log('🚀 UserManagement mounted')

  // Check authentication on component mount
  if (!authStore.isAuthenticated) {
    console.log('🔐 Checking for token in storage...')
    const token = getAuthToken()

    if (!token) {
      console.log('❌ No token found, redirecting to login...')
      router.push('/')
      return
    }

    console.log('✅ Token found, checking admin status...')
  }

  if (authStore.user?.userType !== 'admin') {
    console.log('❌ Not an admin, checking token payload...')
    const token = getAuthToken()
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]))
        console.log('🔓 Token payload:', payload)
        if (payload.userType !== 'admin') {
          console.log('❌ Token is not admin, redirecting...')
          router.push('/dashboard')
          return
        }
      } catch (error) {
        console.log('❌ Cannot decode token:', error)
      }
    } else {
      router.push('/dashboard')
      return
    }
  }

  // Only fetch users if authenticated and admin
  fetchUsers()
})
</script>

<template>
  <AdminLayout>
    <HeadingDefault heading="User Management" />

    <!-- Debug buttons -->
    <div v-if="isDevMode" class="mb-4 flex gap-2">
      <BaseButton
        text="Test Login"
        size="xss"
        variant="ghost"
        @click="testLogin"
        :disabled="loading"
      />
      <BaseButton
        text="Debug Auth"
        size="xss"
        variant="ghost"
        @click="debugAuth"
        :disabled="loading"
      />
      <BaseButton
        text="Test Connection"
        size="xss"
        variant="ghost"
        @click="testBackendConnection"
        :disabled="loading"
      />
    </div>

    <!-- User Stats Cards using StatsCards component -->
    <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4 mb-6">
      <StatsCards
        title="Total Users"
        :value="userStats.total"
        :icon="UsersGroupIcon"
      />
      <StatsCards
        title="Administrators"
        :value="userStats.admin"
        :icon="AdminIcon"
      />
      <StatsCards
        title="Agents"
        :value="userStats.agent"
        :icon="AgentIcon"
      />
      <StatsCards
        title="Engineers"
        :value="userStats.engineer"
        :icon="EngineerIcon"
      />
      <StatsCards
        title="Clients"
        :value="userStats.client"
        :icon="ClientIcon"
      />
      <StatsCards
        title="Active"
        :value="userStats.active"
        :icon="CheckIcon"
      />
      <StatsCards
        title="Inactive"
        :value="userStats.inactive"
        :icon="PauseIcon"
      />
      <StatsCards
        title="Pending Setup"
        :value="userStats.pending"
        :icon="ClockIcon"
      />
    </div>

    <!-- User Management Table -->
    <CardComponent
      className="max-w-full"
      footerClass="justify-end"
      title="Users"
      :showHeaderBorder="true"
      :showHeaderPaddingBottom="true"
      bodyClass="flex flex-col"
    >
      <template #header>
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div class="flex flex-wrap gap-2">
            <!-- ✅ New Company Filter -->
            <SelectOptionPrime
              v-model="companyFilter"
              :options="companyOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Filter by Company"
              id="company-filter"
              size="xss"
              class="w-40"
              @update:modelValue="applyFilters"
            />

            <SelectOptionPrime
              v-model="userTypeFilter"
              :options="userTypeOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Filter by User Type"
              id="user-type-filter"
              size="xss"
              class="w-40"
              @update:modelValue="applyFilters"
            />

            <SelectOptionPrime
              v-model="statusFilter"
              :options="statusOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Filter by Status"
              id="status-filter"
              size="xss"
              class="w-40"
              @update:modelValue="applyFilters"
            />

            <TextInputDefault
              v-model="searchFilter"
              placeholder="Search users..."
              id="searchusers"
              name="searchusers"
              type="text"
              className="w-48"
              size="xss"
              @update:modelValue="applyFilters"
            />
          </div>

          <div class="flex gap-2">
            <BaseButton
              text="Clear Filters"
              size="xss"
              variant="ghost"
              @click="clearFilters"
              :disabled="!searchFilter && !userTypeFilter && !statusFilter && !companyFilter"
            />
            <BaseButton
              text="Add User"
              size="xss"
              @click="openAddUserModal"
              :disabled="loading"
            />
            <BaseButton
              :icon="RefreshIcon"
              size="xss"
              @click="refreshUsers"
              :disabled="loading"
            />
          </div>
        </div>
      </template>

      <template #body>
        <!-- Loading State -->
        <LoaderDefault v-if="loading" title="Loading users..." />

        <!-- Error State -->
        <ErrorState
          v-if="error"
          :message="error"
          hint="Make sure backend is running"
          :show-test-login="true"
          @retry="fetchUsers"
          @test-login="testLogin"
        />

        <!-- No Data State -->
        <EmptyState
          v-else-if="filteredUsers.length === 0 && !loading"
          :icon="UserGroupIcon"
          title="No users found"
          :description="searchFilter || userTypeFilter || statusFilter || companyFilter ? 'No users match your filter criteria' : 'No user found'"
          hint="No users found. Click Add User to create your first user"
          action-text="Add First User"
          @action="openAddUserModal"
        />

        <!-- User Table -->
        <DataTable
          v-else-if="!loading"
          :value="filteredUsers"
          class="w-full"
          sortMode="multiple"
          scrollable
          scrollHeight="500px"
          resizableColumns
          columnResizeMode="fit"
          showGridlines
          :pt="{
            table: { style: 'border-collapse: separate; border-spacing: 0;' },
            thead: { style: 'position: sticky; top: 0; z-index: 20;' },
            tbody: { style: 'position: relative; z-index: 10;' },
          }"
        >
          <!-- User Name Column -->
          <Column sortable :sortField="(data) => getDisplayName(data)" header="User">
            <template #body="{ data }">
              <div class="flex items-center gap-3">
                <div
                  class="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600"
                >
                  <!-- <img
                    v-if="data.profileImage"
                    :src="data.profileImage"
                    alt="User profile"
                    class="object-cover w-full h-full rounded-full"
                  /> -->
                  <span class="flex items-center justify-center w-full h-full text-xs font-medium text-gray-600 dark:text-gray-300">
                    {{ getInitials(data) }}
                  </span>
                </div>

                <div class="flex flex-col">
                  <span class="text-xs font-medium">{{ getDisplayName(data) }}</span>
                  <span class="text-xs text-gray-500">{{ data.email }}</span>
                  <span v-if="data.phone" class="text-xs text-gray-400 mt-1">
                    {{ data.phone }}
                  </span>
                  <span v-if="data.username" class="text-xs text-gray-400 mt-1">
                    @{{ data.username }}
                  </span>
                </div>
              </div>
            </template>
          </Column>

          <!-- User Type Column -->
          <Column sortable field="userType" header="User Type">
            <template #body="{ data }">
              <div class="flex flex-col gap-1">
                <Badge
                  :value="getUserTypeLabel(data.userType)"
                  :class="getUserTypeBadgeClass(data.userType)"
                  class="px-3 py-1 rounded-full text-xs font-medium"
                />
                <span v-if="data.department" class="text-xs text-gray-500">
                  {{ data.department }}
                </span>
              </div>
            </template>
          </Column>

          <!-- ✅ Company Column (NEW) -->
          <Column sortable field="company" header="Company">
            <template #body="{ data }">
              <CompanyBadge :company="data.company" />
            </template>
          </Column>

          <!-- Status Column -->
          <Column sortable field="status" header="Status">
            <template #body="{ data }">
              <div class="flex flex-col gap-1">
                <Badge
                  :value="getStatusLabel(data.status)"
                  :class="getStatusBadgeClass(data.status)"
                  class="px-3 py-1 rounded-full text-xs font-medium"
                />
                <!-- Show expiration info for pending users -->
                <div v-if="data.status === 'pending' && data.invitationExpiresAt" class="text-xs text-gray-500">
                  Expires: {{ formatInvitationExpiration(data.invitationExpiresAt) }}
                </div>
              </div>
            </template>
          </Column>

          <!-- Created On Column -->
          <Column sortable field="createdAt" header="Created On">
            <template #body="{ data }">
              <div class="flex flex-col text-xs">
                {{ formatDate(data.createdAt) }}
              </div>
            </template>
          </Column>

          <!-- Last Login Column -->
          <Column sortable field="lastLogin" header="Last Login">
            <template #body="{ data }">
              <div class="flex flex-col text-xs">
                <span>{{ formatDateTime(data.lastLogin || '') }}</span>
              </div>
            </template>
          </Column>

          <!-- Actions Column -->
          <Column frozen field="actions" header="Actions">
            <template #body="{ data }">
              <div class="flex gap-2">
                <!-- Edit button (always visible) -->
                <!-- <BaseButton
                  size="xs"
                  :aria-label="`edit ${data.firstName} ${data.lastName}`"
                  @click="openEditUserModal(data)"
                  variant="primary"
                  tooltip="Edit User"
                  tooltipPosition="bottom"
                  :icon="EditIcon"
                /> -->

                <!-- Status Toggle button (hidden for pending users) -->
                <BaseButton
                  v-if="data.status !== 'pending' && getToggleIcon(data.status)"
                  size="xss"
                  :aria-label="`${data.status === 'active' ? 'deactivate' : 'activate'} ${getUserName(data, 'firstName')} ${getUserName(data, 'lastName')}`"
                  @click="openToggleModal(data)"
                  :variant="data.status === 'active' ? 'danger' : 'success'"
                  :tooltip="getToggleTooltip(data.status)"
                  tooltipPosition="bottom"
                  :icon="getToggleIcon(data.status)"
                />

                <!-- Resend invitation button for pending users -->
                <BaseButton
                  v-if="data.status === 'pending'"
                  size="xss"
                  :aria-label="`resend invitation to ${getUserName(data, 'firstName')} ${getUserName(data, 'lastName')}`"
                  @click="openResendInvitationModal(data)"
                  variant="ghost"
                  tooltip="Resend Invitation"
                  tooltipPosition="bottom"
                  :icon="CheckIcon"
                  class="text-blue-600 hover:text-blue-800"
                />

                <!-- Permanent delete button (only for inactive users) -->
                <BaseButton
                  v-if="data.status === 'inactive'"
                  size="xss"
                  :aria-label="`permanently delete ${getUserName(data, 'firstName')} ${getUserName(data, 'lastName')}`"
                  @click="openPermanentDeleteModal(data)"
                  variant="danger"
                  tooltip="Permanently Delete"
                  tooltipPosition="bottom"
                  :icon="DeleteIcon"
                  class="text-red-600 hover:text-red-800"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>

      <template #footer v-if="!loading && !error && filteredUsers.length > 0">
        <div class="text-xs text-gray-500">
          Showing {{ filteredUsers.length }} of {{ users.length }} users
          <span class="ml-2">• Source: Database API</span>
          <span class="ml-2">• Invitation links expire in 24 hours</span>
        </div>
      </template>
    </CardComponent>
  </AdminLayout>

  <!-- Status Toggle Modal (activate/deactivate) -->
  <ModalDefault
    v-model="showDeleteModal"
    :title="userToToggle ?
      (userToToggle.status === 'active'
        ? `Deactivate User: ${getUserName(userToToggle, 'firstName')} ${getUserName(userToToggle, 'lastName')}`
        : `Activate User: ${getUserName(userToToggle, 'firstName')} ${getUserName(userToToggle, 'lastName')}`)
      : ''"
    width="max-w-md"
    @close="closeToggleModal"
  >
    <DeleteUserModal
      :user="userToToggle"
      :loading="loading"
    />

    <template #footer>
      <div class="flex justify-end gap-3">
        <BaseButton
          text="Cancel"
          variant="secondary"
          @click="closeToggleModal"
          :disabled="loading"
        />
        <BaseButton
          :text="userToToggle?.status === 'active' ? 'Deactivate User' : 'Activate User'"
          :variant="userToToggle?.status === 'active' ? 'danger' : 'primary'"
          @click="handleToggleStatus"
          :disabled="loading"
        />
      </div>
    </template>
  </ModalDefault>

  <!-- Permanent Delete Modal -->
  <ModalDefault
    v-model="showPermanentDeleteModal"
    :title="`Permanently Delete User: ${userToPermanentDelete ? getUserName(userToPermanentDelete, 'firstName') : ''} ${userToPermanentDelete ? getUserName(userToPermanentDelete, 'lastName') : ''}`"
    width="max-w-md"
    classBody="my-4"
    @close="closePermanentDeleteModal"
  >
    <PermanentDeleteModal
      :user="userToPermanentDelete"
      :loading="loading"
      :error="error"
    />

    <template #footer>
      <div class="flex justify-end gap-3">
        <BaseButton
          text="Cancel"
          variant="secondary"
          @click="closePermanentDeleteModal"
          :disabled="loading"
        />
        <BaseButton
          text="Permanently Delete User"
          variant="danger"
          @click="handlePermanentDeleteUser"
          :disabled="loading"
        />
      </div>
    </template>
  </ModalDefault>

  <!-- Resend Invitation Modal -->
  <ModalDefault
    v-model="showResendInvitationModal"
    :title="`Resend Invitation: ${userToResendInvitation ? getUserName(userToResendInvitation, 'firstName') : ''} ${userToResendInvitation ? getUserName(userToResendInvitation, 'lastName') : ''}`"
    width="max-w-md"
    classBody="my-4"
    @close="closeResendInvitationModal"
  >
      <p class="text-gray-700 ">
        Resend invitation email to
        <span class="font-semibold">{{ userToResendInvitation?.email }}</span>?
        The invitation will be valid for 24 hours.
      </p>

    <template #footer>
      <div class="flex justify-end gap-3">
        <BaseButton
          text="Cancel"
          variant="secondary"
          @click="closeResendInvitationModal"
          :disabled="loading"
        />
        <BaseButton
          text="Resend Invitation"
          variant="primary"
          @click="handleResendInvitation"
          :disabled="loading"
        />
      </div>
    </template>
  </ModalDefault>

  <!-- Generic Confirmation Modal -->
  <ModalDefault
    v-model="showConfirmationModal"
    title="Confirmation"
    width="max-w-md"
    classBody="my-4"
    @close="closeConfirmationModal"
  >
    <div class="p-4">
      <p class="text-gray-700 mb-4">{{ confirmationMessage }}</p>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3">
        <BaseButton
          text="Cancel"
          variant="secondary"
          @click="closeConfirmationModal"
          :disabled="loading"
        />
        <BaseButton
          text="Confirm"
          variant="primary"
          @click="handleConfirmation"
          :disabled="loading"
        />
      </div>
    </template>
  </ModalDefault>

  <!-- Add/Edit User Modal -->
  <ModalDefault
    v-model="showUserModal"
    :title="
      selectedUser
        ? `Edit User: ${getUserName(selectedUser, 'firstName')} ${getUserName(selectedUser, 'lastName')}`
        : 'Add New User'
    "
    width="max-w-2xl"
    @close="closeUserModal"
  >
    <UserModal
      ref="userModalRef"
      :user="selectedUser"
      :loading="loading"
      :apiError="apiError"
    />

    <template #footer>
      <div class="flex justify-end gap-3">
        <BaseButton
          text="Cancel"
          variant="secondary"
          @click="closeUserModal"
          :disabled="loading"
        />
        <BaseButton
          :text="selectedUser ? 'Change User Type' : 'Create User'"
          variant="primary"
          @click="handleSaveUser"
          :disabled="loading"
        />
      </div>
    </template>
  </ModalDefault>
</template>

<style scoped>
.bg-purple-100 {
  background-color: #f3e8ff;
}
.bg-blue-100 {
  background-color: #dbeafe;
}
.bg-green-100 {
  background-color: #dcfce7;
}
.bg-orange-100 {
  background-color: #ffedd5;
}
.bg-yellow-100 {
  background-color: #fef9c3;
}
.bg-red-100 {
  background-color: #fee2e2;
}
.bg-gray-100 {
  background-color: #f3f4f6;
}

.text-purple-800 {
  color: #6b21a8;
}
.text-blue-800 {
  color: #1e40af;
}
.text-green-800 {
  color: #166534;
}
.text-orange-800 {
  color: #9a3412;
}
.text-yellow-800 {
  color: #854d0e;
}
.text-red-800 {
  color: #991b1b;
}
.text-gray-800 {
  color: #374151;
}

.data-highlight {
  font-size: 1.875rem;
  font-weight: 700;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.text-blue-600 {
  color: #2563eb;
}
.text-blue-600:hover {
  color: #1d4ed8;
  animation: pulse 2s infinite;
}
</style>
